import * as React from 'react';
import classnames from 'classnames';
import { Key } from 'ts-key-enum';
import { ownerDocument } from '@material-ui/core/utils';
import { makeStyles, createStyles } from '../../styles';
import { useComboboxContext } from '../Combobox.context';
import type { ComboboxGroup, ComboboxOption, ComboboxProps } from '../Combobox.types';

export const useStyles = makeStyles(<
  Option extends ComboboxOption,
  Group extends ComboboxGroup<Option>,
>() => createStyles({
  list: {
    margin: 0,
    padding: 0,
  },
  listbox: {
    maxHeight: '50vh',
    overflowY: 'auto',
    '& > li:first-child': {
      // Round top corners when not searchable
      borderRadius: ({ isSearchable }: Partial<ComboboxProps<Option, Group>>) => (!isSearchable ? '1px 1px 0 0' : undefined),
    },
    '& > li:last-child': {
      // Round bottom corners
      borderRadius: '0 0 1px 1px',
    },
  },
}));

/**
 * The keyboard focus handling provided by MUI's MenuList component does not include
 * built in support for nested lists (e.g. ul > li > ul > li).
 * Since we rely on the nested list html structure for grouped options, we need to add
 * the keyboard focus handling on our end.
 *
 * The is heavily modeled after MUI MenuList.
 * https://github.com/mui-org/material-ui/blob/v4.x/packages/material-ui/src/MenuList/MenuList.js
 */

// Utility function to return the element as type HTMLElement.
function returnFocusableOption(option: ChildNode | null | undefined) {
  // Type ChildNode could represent a non-focusable element (e.g. svg).
  return option ? option as HTMLElement : null;
}

// Utility function to check if the node exists and nodeName is UL.
function nodeIsGroup(node?: ChildNode | null | undefined) {
  return node?.nodeName === 'UL';
}

// Returns the first focusable option of the supplied group.
function firstGroupItem(group: ChildNode): HTMLElement | null {
  const firstOption = returnFocusableOption(group.firstChild?.firstChild);

  if (firstOption && !firstOption?.hasAttribute('tabindex')) {
    return returnFocusableOption(firstOption.nextElementSibling);
  }

  return returnFocusableOption(firstOption);
}

// Returns the last focusable option of the supplied group.
function lastGroupItem(group: ChildNode): HTMLElement | null {
  const lastOption = returnFocusableOption(group.lastChild?.lastChild);

  if (lastOption && !lastOption?.hasAttribute('tabindex')) {
    return returnFocusableOption(lastOption.previousElementSibling);
  }

  return returnFocusableOption(lastOption);
}

// Returns the first option of the supplied list.
function firstItem(list: HTMLUListElement) {
  const optionOrGroup = list.firstChild;

  // When the first child is a group, return the first focusable option.
  if (optionOrGroup && nodeIsGroup(optionOrGroup?.firstChild)) {
    return firstGroupItem(optionOrGroup);
  }

  return returnFocusableOption(optionOrGroup);
}

// Returns the last option of the supplied list.
function lastItem(list: HTMLUListElement) {
  const optionOrGroup = list.lastChild;

  // When the last child is a group, return the last focusable option.
  if (optionOrGroup && nodeIsGroup(optionOrGroup?.lastChild)) {
    return lastGroupItem(optionOrGroup);
  }

  return returnFocusableOption(optionOrGroup);
}

// Returns the next focusable option.
function nextItem(list: HTMLUListElement, item: Element, disableListWrap?: boolean) {
  if (item.nextElementSibling) {
    if (nodeIsGroup(item.nextElementSibling?.firstChild)) {
      return firstGroupItem(item.nextElementSibling);
    }

    return returnFocusableOption(item.nextElementSibling);
  }

  const currentGroup = item.parentElement?.parentElement;
  const next = currentGroup?.nextElementSibling;

  if (next?.firstChild) {
    return nodeIsGroup(next?.firstChild) ? firstGroupItem(next) : returnFocusableOption(next);
  }

  return disableListWrap ? null : firstItem(list);
}

// Returns the previous focusable option.
function previousItem(list: HTMLUListElement, item: Element, disableListWrap?: boolean) {
  if (item.previousElementSibling) {
    if (nodeIsGroup(item.previousElementSibling?.firstChild)) {
      return lastGroupItem(item.previousElementSibling);
    }

    if (item.previousElementSibling.hasAttribute('tabindex')) {
      return returnFocusableOption(item.previousElementSibling);
    }
  }

  const currentGroup = item.parentElement?.parentElement;
  const previous = currentGroup?.previousElementSibling;

  if (previous?.firstChild) {
    return nodeIsGroup(previous?.firstChild) ? lastGroupItem(previous) : returnFocusableOption(previous);
  }

  return disableListWrap ? null : lastItem(list);
}

export const Listbox = <Option extends ComboboxOption, Group extends ComboboxGroup<Option>>() => {
  const {
    comboboxProps,
    filteredOptions,
    components,
    listboxHasFocus,
    isOptionSelected,
    selectOption,
    deselectOption,
    searchString,
    setMenuIsOpen,
  } = useComboboxContext<Option, Group>();
  const { id, isMulti, strings, showSelectedOptions, isLoading, isSearchable, ListboxProps } = comboboxProps;
  const { MenuItem, NoResultsMenuItem, LoadingResultsMenuItem, MenuListSubheader } = components;
  const { noResults, loadingResults } = strings;
  const listRef = React.useRef<HTMLUListElement>(null);
  const containsFocus = React.useRef<boolean>();
  const classes = useStyles({ isSearchable });

  function handleSelectOption(option: Option, preventMenuClose?: boolean) {
    if (isOptionSelected(option)) {
      deselectOption(option, preventMenuClose);
    } else {
      selectOption(option, preventMenuClose);
    }
  }

  function handleOnMouseUp(option: Option) {
    handleSelectOption(option);
  }

  function handleOnKeyDown(event: React.KeyboardEvent<HTMLLIElement>, option: Option) {
    const list = listRef.current;

    if (!list) {
      return;
    }

    /**
     * https://github.com/mui-org/material-ui/blob/v4.x/packages/material-ui/src/MenuList/MenuList.js#L149
     * type Element - will always be defined since we are in a keydown handler
     * attached to an element. A keydown event is either dispatched to the activeElement
     * or document.body or document.documentElement. Only the first case will
     * trigger this specific handler.
     */
    const currentFocus = ownerDocument(list).activeElement as Element;

    switch (event.key) {
      // Spacebar
      case ' ':
        // Prevent menu close on select when isMulti.
        handleSelectOption(option, isMulti);
        break;
      case Key.Enter:
        setMenuIsOpen(false);
        break;
      case Key.ArrowUp:
        previousItem(list, currentFocus, false)?.focus();
        event.preventDefault(); // prevent page scroll
        break;
      case Key.ArrowDown:
        nextItem(list, currentFocus, false)?.focus();
        event.preventDefault(); // prevent page scroll
        break;
    }
  }

  function filterSelected(option: Option) {
    return !(!showSelectedOptions && isOptionSelected(option));
  }

  function filterGroups(optionOrGroup: Option | Group) {
    if ('options' in optionOrGroup) {
      const groupOptions = optionOrGroup.options.filter(filterSelected);

      return groupOptions.length ? { ...optionOrGroup, options: groupOptions } : [] as never[];
    }

    return filterSelected(optionOrGroup) ? optionOrGroup : [] as never[];
  }

  function renderOption(optionOrGroup: Option | Group, groupLabel?: string) {
    if ('options' in optionOrGroup) {
      const subHeaderId = `combobox-subheader-${optionOrGroup.label}`;

      return (
        <li key={subHeaderId}>
          <ul
            role="group"
            aria-labelledby={subHeaderId}
            className={classes.list}
          >
            <MenuListSubheader id={subHeaderId} key={optionOrGroup.label}>
              {optionOrGroup.label}
            </MenuListSubheader>
            {optionOrGroup.options.map(option => renderOption(option, optionOrGroup.label))}
          </ul>
        </li>
      );
    }

    const option = optionOrGroup as Option;

    return (
      <MenuItem
        key={`${groupLabel}-${option.value}`}
        option={option}
        // MUI (ButtonBase) calls onClick through onKeyDown without offering an escape hatch - Use onMouseUp to bypass.
        onMouseUp={() => handleOnMouseUp(option)}
        onKeyDown={(event: React.KeyboardEvent<HTMLLIElement>) => handleOnKeyDown(event, option)}
        selected={isOptionSelected(option)}
      >
        {option.label}
      </MenuItem>
    );
  }

  React.useEffect(
    () => {
      // Handle initial focus
      if (listboxHasFocus && !containsFocus.current && listRef.current) {
        firstItem(listRef.current)?.focus();
        containsFocus.current = true;
      } else {
        containsFocus.current = false;
      }
    },
    [listboxHasFocus],
  );

  return (
    <ul
      ref={listRef}
      id={`${id}-menu`}
      role="listbox"
      aria-labelledby={`${id}-label`}
      aria-multiselectable={isMulti}
      {...ListboxProps}
      // Declare after spread to merge consumer styles with internal styles.
      className={classnames(
        classes.list,
        classes.listbox,
        ListboxProps?.className,
      )}
    >
      {isLoading && loadingResults &&
        <LoadingResultsMenuItem role="option">{loadingResults}</LoadingResultsMenuItem>
      }
      {!isLoading && !filteredOptions.length && searchString &&
        <NoResultsMenuItem role="option">{noResults(searchString)}</NoResultsMenuItem>
      }
      {!isLoading && filteredOptions.flatMap(filterGroups).map(option => renderOption(option))}
    </ul>
  );
};

export default Listbox;
